import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, map, catchError, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { endpoints } from '../../environments/endpoints';
import { AuthResponse, LaravelDefaultResponse } from '../Interfaces/response.interface';
import { User } from '../Models/user.model';
import { AuthTokenService } from './auth-token.service';
import { ApiService } from './api.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _api = inject(ApiService);
  private _router = inject(Router);
  private _authTokenService = inject(AuthTokenService);
  private _localStorage = inject(LocalStorageService);
  private authInitialization$?: Observable<boolean>;

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private authInitializedSubject = new BehaviorSubject<boolean>(false);
  public authInitialized$ = this.authInitializedSubject.asObservable();

  initializeAuth(): Observable<boolean> {
    if (this.authInitializedSubject.value) {
      return of(this.isAuthenticatedSubject.value);
    }

    if (!this.authInitialization$) {
      this.authInitialization$ = this.bootstrapAuth().pipe(
        map((user) => !!user),
        catchError(() => of(false)),
        tap((is_authenticated) => {
          this.authInitializedSubject.next(true);
          this.isAuthenticatedSubject.next(is_authenticated);
        }),
        shareReplay(1)
      );
    }

    return this.authInitialization$;
  }

  private bootstrapAuth(): Observable<User | null> {
    if (this._authTokenService.hasValidToken()) {
      return this.me().pipe(
        catchError((error) => this.handleInitializationError(error))
      );
    }

    if (this._authTokenService.hasRefreshToken()) {
      return this.refreshToken().pipe(
        switchMap(() => this.me()),
        catchError((error) => this.handleInitializationError(error))
      );
    }

    this.clearAuth();
    return of(null);
  }

  private handleInitializationError(error: unknown): Observable<null> {
    if (this.isAuthenticationError(error)) {
      this.clearAuth();
    }

    return of(null);
  }

  private isAuthenticationError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return true;
    }

    return error.status === 401 || error.status === 403;
  }

  setReturnUrl(returnUrl: string): void {
    this._authTokenService.setReturnUrl(returnUrl);
  }

  exchangeCode(code: string): Observable<User> {
    return this._api.post<LaravelDefaultResponse<AuthResponse>>(
      endpoints.auth.exchangeCode,
      { code },
      { skipLoading: true },
    ).pipe(
      tap(response => {
        this.setAuth(response.data);
      }),
      switchMap(() => this.me())
    );
  }

  logout(): void {
    this.revokeRefreshToken().subscribe({
      next: () => {
        this.clearAuth();
        this._router.navigate(['/login']);
      }
    });
  }

  terminateSession(): Observable<boolean> {
    return this.revokeRefreshToken().pipe(
      tap(() => {
        this.clearAuth();
        this._localStorage.clear();
        this._localStorage.clearSessionStorage();
      })
    );
  }

  private revokeRefreshToken(): Observable<boolean> {
    const refresh_token = this._authTokenService.getRefreshToken();

    if (!refresh_token) {
      return of(false);
    }

    return this._api.post(
      endpoints.auth.logout,
      { refresh_token },
      { skipLoading: true },
    ).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  me(): Observable<User> {
    return this._api.get<LaravelDefaultResponse<User>>(
      endpoints.onboarding.me,
      { skipLoading: true },
    ).pipe(
      map(response => new User(response.data)),
      tap(user => {
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);
      })
    );
  }

  setAuth(auth_data: AuthResponse): void {
    this._authTokenService.setToken(auth_data);
    this.isAuthenticatedSubject.next(true);
    this.authInitializedSubject.next(true);
  }

  getToken(): string | null {
    return this._authTokenService.getToken();
  }

  isTokenExpired(): boolean {
    return this._authTokenService.isTokenExpired();
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value && this._authTokenService.hasValidToken();
  }

  refreshToken(): Observable<AuthResponse> {
    const refresh_token = this._authTokenService.getRefreshToken();

    if (!refresh_token) {
      this.clearAuth();
      return of(null as unknown as AuthResponse);
    }

    return this._api.post<LaravelDefaultResponse<AuthResponse>>(
      endpoints.auth.refresh,
      { refresh_token },
      { skipLoading: true },
    ).pipe(
      map(response => response.data),
      tap(auth_data => {
        this.setAuth(auth_data);
      })
    );
  }

  getAndClearReturnUrl(): string {
    return this._authTokenService.getAndClearReturnUrl();
  }

  clearAuth(): void {
    this._authTokenService.clearToken();
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }
}
